/**
 * Piston engine and fixed-pitch propeller.
 *
 * ## RPM is a state, not a control
 *
 * The engine and propeller are coupled through a real rotational degree of freedom:
 *
 *     I·dω/dt = Q_engine(rpm, throttle, ρ) − Q_propeller(J, rpm, ρ)
 *
 * integrated on the same 120 Hz tick as everything else. That single equation produces, with
 * no special-casing, all the behaviour that makes a fixed-pitch aeroplane feel the way it does:
 *
 *   - RPM **rises with airspeed** as the propeller unloads in a dive, and sags in the climb.
 *   - Static RPM at full throttle is well below redline (about 2350 for this trainer), so the
 *     engine cannot make rated power until the aircraft is moving.
 *   - Closing the throttle at speed leaves the propeller **windmilling**: past the zero-thrust
 *     advance ratio the thrust coefficient goes negative and the propeller becomes a large
 *     disc brake, which is most of the drag in a glide.
 *
 * ## Propeller coefficients
 *
 * Standard non-dimensional form, with `n` in revolutions per second and `J = V/(nD)`:
 *
 *     T = C_T(J)·ρ·n²·D⁴      P = C_P(J)·ρ·n³·D⁵      η = J·C_T/C_P
 *
 * ## The four left-turning tendencies
 *
 * All four are modelled, because between them they are why a trainer needs right rudder on
 * take-off and why the acceptance criteria ask for "torque pulls left":
 *
 *   1. **Torque reaction** — the airframe feels `−Q` about the roll axis.
 *   2. **P-factor** — at positive alpha the descending blade meets the air at a higher angle
 *      and makes more thrust, moving the thrust centre sideways and yawing the nose.
 *   3. **Slipstream swirl** — the corkscrewing wake strikes one side of the fin.
 *   4. **Slipstream axial velocity** — more dynamic pressure over the inner wing and tail,
 *      which is why the elevator bites at taxi speed with power on.
 *
 * `updateFixed(dt)`: yes, at 120 Hz. Allocation-free.
 */

import { Curve1D } from './aero/tables.js';
import { ISA_SEA_LEVEL_DENSITY, type AtmosphereSample } from './atmosphere.js';
import { clamp } from '../core/math.js';
import { makePropulsionOutput, type Powerplant, type PropulsionOutput } from './powerplant.js';

// Re-exported because this module used to own them and half the simulator imports them from
// here. They live in `powerplant.ts` now, alongside the interface that made them shared.
export { makePropulsionOutput, type PropulsionOutput } from './powerplant.js';

export interface PistonEngineConfig {
  /** Rated shaft power at `ratedRpm`, sea level, full throttle [W]. */
  ratedPowerW: number;
  ratedRpm: number;
  /** Redline [rpm]. */
  maxRpm: number;
  /** Power fraction of rated as a function of RPM fraction of rated, at full throttle. */
  powerCurveRpmFraction: readonly number[];
  powerCurvePowerFraction: readonly number[];
  /**
   * Critical altitude of the supercharger [m]. **Omit for a normally aspirated engine.**
   *
   * The altitude up to which the blower can still hold sea-level manifold pressure, and therefore
   * rated power. Below it the engine makes what it makes on the ground; above it, it lapses like
   * any other. A trainer's Lycoming has no blower and omits this; a two-stage Merlin's high gear
   * is good to about 7,600 m, which is why a Mustang has any business at 25,000 ft at all.
   */
  superchargerCriticalAltitudeM?: number;
  /** Throttle authority floor: the fraction of available power produced at closed throttle. */
  idlePowerFraction: number;
  /** Rotating inertia of crankshaft plus propeller [kg·m²]. */
  inertiaKgM2: number;
  /** Propeller rotation seen from the cockpit: +1 clockwise (typical Lycoming), −1 otherwise. */
  rotationSense: number;
}

export interface PropellerConfig {
  diameterM: number;
  /** Thrust coefficient against advance ratio. */
  advanceRatio: readonly number[];
  thrustCoefficient: readonly number[];
  /** Power coefficient against the same advance ratios. */
  powerCoefficient: readonly number[];
  /**
   * Propeller revolutions per engine revolution. **Defaults to 1** — direct drive.
   *
   * A trainer's Lycoming is bolted straight to its propeller, so one shaft speed describes both
   * and this model had only one. A high-power piston cannot do that: a Merlin turns 3,000 rpm and
   * its propeller 1,440, because a 3.4 m blade at 3,000 rpm has supersonic tips. Without the
   * reduction the model has to be lied to — either the tachometer reads the propeller and no
   * Merlin's does, or the coefficient tables are scaled by a factor that means nothing.
   *
   * It is not only bookkeeping. Torque is multiplied by exactly what speed is divided by, so a
   * geared engine's propeller torque — and therefore the roll the airframe feels — is `1/ratio`
   * times the crank's. That is *the* reason a Mustang's torque reaction is famously vicious and a
   * 172's is a footnote, and it falls straight out of this number rather than being dialled in.
   */
  reductionRatio?: number;
  /**
   * P-factor strength: the lateral thrust-centre offset per radian of prop-disc alpha, as a
   * fraction of the diameter. 0.1 gives a trainer's familiar right-rudder-on-climb-out.
   */
  pFactorGain: number;
  /** Slipstream swirl strength [-]. Scales the tangential velocity delivered to the fin. */
  swirlGain: number;
}

/** Below this shaft speed the propeller is treated as stopped, to keep `J` finite. */
const MIN_REV_PER_SECOND = 0.5;

/**
 * Shaft speed at which a cranking engine catches and starts firing [rpm].
 *
 * A real piston aero engine fires somewhere around 150–250 rpm on the starter, which is why a
 * start is *heard* as two distinct events — the slow heavy churn of the starter dragging it over
 * compression, and then the moment it picks up and runs away to idle under its own power.
 */
const CATCH_RPM = 220;

/** Starter motor torque [N·m]. Enough to crank a 180 hp four to catch speed in about a second. */
const STARTER_TORQUE_NM = 55;

/**
 * Drag torque of an engine that is turning but not firing [N·m], as `a + b·rpm`.
 *
 * Compression and friction. The constant term is what a starter has to overcome before anything
 * turns at all; the rate term is why a stopped engine winds down in a second or two rather than
 * coasting. Neither is a published figure — they are chosen so that the *start* takes the two and
 * a half seconds a real one does, which is the only property of them anybody can perceive.
 */
const DRAG_CONSTANT_NM = 18;
const DRAG_PER_RPM = 0.012;

export class Propulsion implements Powerplant {
  private readonly engine: PistonEngineConfig;
  private readonly propeller: PropellerConfig;
  /** Propeller revolutions per engine revolution; 1 on a direct-drive engine. */
  private readonly gearRatio: number;
  /**
   * Density ratio at the supercharger's critical altitude, or 0 when there is no blower.
   *
   * Held as a *density ratio* rather than an altitude because that is the form the power lapse
   * needs, and converting once at construction keeps a `Math.pow` out of a function the trim
   * solver's bisection calls hundreds of times per solve.
   */
  private readonly criticalDensityRatio: number;
  private readonly powerCurve: Curve1D;
  private readonly thrustCurve: Curve1D;
  private readonly powerCoefficientCurve: Curve1D;
  private readonly discAreaM2: number;

  /** Shaft speed [rev/s]. */
  private revPerSecond: number;

  /**
   * Is the ignition on? False is mags off — the engine will not fire and will not restart.
   *
   * A field written by the flight model from the control commands each tick, rather than an
   * argument, so that `updateFixed`'s six-parameter signature does not become an eight-parameter
   * one for two booleans that change about twice a flight.
   */
  ignitionOn = true;

  /** Is the starter motor engaged? Held, like a real key. */
  starterEngaged = false;

  /**
   * Is the engine actually firing?
   *
   * Starts true, which is deliberate: an aeroplane spawns running. Every existing test, the
   * trim solver, the validation gates, `npm run perf` and the scripted screenshots all assume a
   * live engine on the first tick, and a milestone that added an audio feature by making the
   * aeroplane spawn dead would be a milestone that broke the simulator.
   */
  private runningValue = true;

  readonly output = makePropulsionOutput();

  constructor(engine: PistonEngineConfig, propeller: PropellerConfig) {
    this.engine = engine;
    this.propeller = propeller;
    this.gearRatio = this.propeller.reductionRatio ?? 1;
    /*
     * ISA density ratio at the critical altitude. σ = (1 − 2.25577e−5·h)^4.2559 up to the
     * tropopause, which is where every critical altitude worth modelling sits.
     */
    const criticalAltitudeM = engine.superchargerCriticalAltitudeM ?? 0;
    this.criticalDensityRatio =
      criticalAltitudeM > 0 ? Math.pow(1 - 2.25577e-5 * criticalAltitudeM, 4.2559) : 0;
    this.powerCurve = new Curve1D(
      engine.powerCurveRpmFraction,
      engine.powerCurvePowerFraction,
      'engine power curve',
    );
    this.thrustCurve = new Curve1D(
      propeller.advanceRatio,
      propeller.thrustCoefficient,
      'propeller thrust coefficient',
    );
    this.powerCoefficientCurve = new Curve1D(
      propeller.advanceRatio,
      propeller.powerCoefficient,
      'propeller power coefficient',
    );
    this.discAreaM2 = (Math.PI * propeller.diameterM * propeller.diameterM) / 4;
    this.revPerSecond = engine.ratedRpm / 60 / 4; // starts at a plausible idle
  }

  get rpm(): number {
    return this.revPerSecond * 60;
  }

  set rpm(value: number) {
    this.revPerSecond = Math.max(0, value) / 60;
  }

  /** True while the engine is firing under its own power. */
  get running(): boolean {
    return this.runningValue;
  }

  /**
   * Cut the ignition and stop the engine.
   *
   * The caller decides when this is allowed — `main.ts` gates it on the wheels being on the
   * ground. That is a genuine limitation rather than a safety rail: the propeller's power
   * coefficient table only covers the range where the propeller absorbs power, so a dead engine
   * in flight winds the shaft to a stop instead of windmilling. Shutting down on the ramp is the
   * case that matters and the case that is right.
   */
  shutDown(): void {
    this.runningValue = false;
    this.ignitionOn = false;
    this.starterEngaged = false;
  }

  /** Put the mags back on and hold the starter. Release it with {@link releaseStarter}. */
  engageStarter(): void {
    this.ignitionOn = true;
    this.starterEngaged = true;
  }

  releaseStarter(): void {
    this.starterEngaged = false;
  }

  /** Put the engine back to a running idle without a start sequence — for spawn and for tests. */
  forceRunning(rpm: number): void {
    this.runningValue = true;
    this.ignitionOn = true;
    this.starterEngaged = false;
    this.rpm = rpm;
  }

  /** Shaft power available at this RPM, throttle and density [W]. */
  enginePowerW(rpm: number, throttle: number, densityKgPerM3: number): number {
    const rpmFraction = rpm / this.engine.ratedRpm;
    const available = this.engine.ratedPowerW * Math.max(0, this.powerCurve.at(rpmFraction));

    /*
     * Gagg–Ferrar: a normally aspirated engine loses power faster than density alone, because
     * friction losses do not fall with altitude.
     *
     * A **supercharged** one does not lose it at all until the blower runs out of the pressure
     * ratio it needs to keep sea-level manifold pressure — its *critical altitude* — and only
     * lapses above that. That is not a detail: a two-stage Merlin is rated at 1,500 hp near
     * 25,000 ft, which is the entire reason a Mustang could escort bombers, and a model that
     * lapses it from sea level gives back 37 % of its power up there and an aeroplane that cannot
     * hold altitude at its own published cruise. Measured before this existed: −2,032 fpm at full
     * throttle at the speed it is supposed to cruise at.
     *
     * Referencing the lapse to the density *at* the critical altitude rather than to sea level is
     * what makes the curve continuous — full power below it, and above it the same Gagg–Ferrar
     * fall-off an unsupercharged engine has, starting from where the blower gave up.
     */
    const sigma = densityKgPerM3 / ISA_SEA_LEVEL_DENSITY;
    const criticalSigma = this.criticalDensityRatio;
    const densityFactor =
      criticalSigma <= 0
        ? Math.max(0, 1.132 * sigma - 0.132)
        : sigma >= criticalSigma
          ? 1
          : Math.max(0, 1.132 * (sigma / criticalSigma) - 0.132);

    const idle = this.engine.idlePowerFraction;
    const demand = idle + (1 - idle) * clamp(throttle, 0, 1);
    return available * densityFactor * demand;
  }

  /**
   * Propeller torque absorbed at this operating point, **referred to the crank** [N·m].
   *
   * Referred, because the caller is balancing it against engine torque on the engine's own shaft.
   * Power is conserved across the gearbox, so `Q_crank = Q_prop · ratio` — the same factor the
   * speed goes the other way by. {@link propellerShaftTorqueNm} is the un-referred number, which
   * is what the *airframe* feels as roll.
   */
  propellerTorqueNm(revPerSecond: number, axialSpeedMps: number, densityKgPerM3: number): number {
    return (
      this.propellerShaftTorqueNm(revPerSecond, axialSpeedMps, densityKgPerM3) * this.gearRatio
    );
  }

  /** Torque at the *propeller* shaft [N·m] — what the air applies and the airframe reacts to. */
  propellerShaftTorqueNm(
    revPerSecond: number,
    axialSpeedMps: number,
    densityKgPerM3: number,
  ): number {
    const n = Math.max(MIN_REV_PER_SECOND, revPerSecond * this.gearRatio);
    const diameter = this.propeller.diameterM;
    const advanceRatio = axialSpeedMps / (n * diameter);
    const cp = this.powerCoefficientCurve.at(advanceRatio);
    // P = Cp·ρ·n³·D⁵ and Q = P/(2πn), so the n³ becomes n².
    return (cp * densityKgPerM3 * n * n * Math.pow(diameter, 5)) / (2 * Math.PI);
  }

  /**
   * Shaft speed at which engine torque balances propeller torque, for a given throttle and
   * airspeed. Used by the trim solver, which needs a steady state rather than a transient.
   * Solved by bisection — the torque difference is monotonic in RPM over the bracket.
   */
  equilibriumRpm(throttle: number, axialSpeedMps: number, air: AtmosphereSample): number {
    const densityKgPerM3 = air.densityKgPerM3;
    const torqueBalance = (rpm: number): number => {
      const omega = (rpm / 60) * 2 * Math.PI;
      if (omega < 1e-6) return 1;
      const engineTorque = this.enginePowerW(rpm, throttle, densityKgPerM3) / omega;
      return engineTorque - this.propellerTorqueNm(rpm / 60, axialSpeedMps, densityKgPerM3);
    };

    let low = 60;
    let high = this.engine.maxRpm * 1.4;
    if (torqueBalance(high) > 0) return high;
    if (torqueBalance(low) < 0) return low;

    for (let i = 0; i < 80; i++) {
      const mid = (low + high) * 0.5;
      if (torqueBalance(mid) > 0) low = mid;
      else high = mid;
    }
    return (low + high) * 0.5;
  }

  /**
   * Advance one tick.
   *
   * @param throttle        0…1.
   * @param axialSpeedMps   Airspeed component along the body x axis at the propeller [m/s].
   * @param alphaRad        Angle of attack at the propeller disc [rad].
   * @param betaRad         Sideslip at the propeller disc [rad].
   * @param air             The atmosphere at the aircraft. Only the density matters to a piston
   *                        engine; the rest of the sample is there because {@link Powerplant}
   *                        also has to serve a turbofan, which needs the speed of sound.
   * @param dtSeconds       Fixed timestep [s].
   */
  updateFixed(
    throttle: number,
    axialSpeedMps: number,
    alphaRad: number,
    betaRad: number,
    air: AtmosphereSample,
    dtSeconds: number,
  ): PropulsionOutput {
    const densityKgPerM3 = air.densityKgPerM3;
    const diameter = this.propeller.diameterM;
    const out = this.output;

    // --- shaft dynamics ---------------------------------------------------------------
    const rpm = this.revPerSecond * 60;
    const omega = this.revPerSecond * 2 * Math.PI;

    /*
     * Three states the shaft can be in, and only one of them existed before M5.
     *
     *   - **Firing.** Exactly what it always was: power from the curve, torque from power.
     *   - **Turning but not firing.** Compression and friction drag it down, and the starter (if
     *     engaged) drives it up. This is the state a start sequence lives in, and it is what
     *     gives the sound of a start its two distinct halves.
     *   - **Stopped.** Falls out of the above once the drag has taken the last of the speed.
     *
     * The catch and the flame-out are checked *before* the integration so that the tick in which
     * the engine picks up is also the first tick it makes power, rather than the one after.
     */
    if (!this.runningValue && this.ignitionOn && rpm >= CATCH_RPM) this.runningValue = true;
    /*
     * The ignition is the *only* thing that stops a running engine here, deliberately.
     *
     * The obvious extra condition — "and dies below some minimum RPM" — is a trap. The trim
     * solver drives this model to arbitrary shaft speeds looking for an equilibrium, and its
     * bisection bracket starts at 60 rpm; a low-RPM flame-out would let the solver stop the
     * engine mid-search and hand the validation gates a silently powerless aeroplane. Mags off
     * already produces the audible behaviour that matters: drag winds the shaft down and it
     * stops.
     */
    if (this.runningValue && !this.ignitionOn) this.runningValue = false;

    const firing = this.runningValue;
    const powerW = firing ? this.enginePowerW(rpm, throttle, densityKgPerM3) : 0;
    let engineTorque = firing
      ? omega > 1e-3
        ? powerW / omega
        : powerW / (2 * Math.PI * MIN_REV_PER_SECOND)
      : -(DRAG_CONSTANT_NM + DRAG_PER_RPM * rpm);
    if (this.starterEngaged) engineTorque += STARTER_TORQUE_NM;

    const propellerTorque = this.propellerTorqueNm(this.revPerSecond, axialSpeedMps, densityKgPerM3);

    const angularAcceleration = (engineTorque - propellerTorque) / this.engine.inertiaKgM2;
    this.revPerSecond = Math.max(0, this.revPerSecond + (angularAcceleration * dtSeconds) / (2 * Math.PI));
    // Never let the model run away past a physically silly overspeed.
    this.revPerSecond = Math.min(this.revPerSecond, (this.engine.maxRpm * 1.5) / 60);

    // --- thrust -----------------------------------------------------------------------
    // At the *propeller's* speed. `revPerSecond` is the crank, and on a geared engine those are
    // not the same number — using the crank here would ask a 3.4 m blade for the thrust of one
    // turning twice as fast, which is most of a megawatt of thrust that does not exist.
    const propellerRevPerSecond = this.revPerSecond * this.gearRatio;
    const n = Math.max(MIN_REV_PER_SECOND, propellerRevPerSecond);
    const advanceRatio = axialSpeedMps / (n * diameter);
    const ct = this.thrustCurve.at(advanceRatio);
    const thrust =
      propellerRevPerSecond < MIN_REV_PER_SECOND
        ? 0
        : ct * densityKgPerM3 * n * n * Math.pow(diameter, 4);

    // --- slipstream --------------------------------------------------------------------
    // Actuator-disc momentum theory: the wake is accelerated to sqrt(V² + 2T/(ρA)).
    let axialIncrement = 0;
    if (thrust > 0) {
      const wake = Math.sqrt(axialSpeedMps * axialSpeedMps + (2 * thrust) / (densityKgPerM3 * this.discAreaM2));
      axialIncrement = wake - axialSpeedMps;
    }

    // Swirl carries the shaft torque into the wake as tangential momentum. The reference
    // radius is a quarter of the diameter, roughly where the blade does most of its work.
    const referenceRadius = diameter * 0.25;
    const wakeSpeed = Math.max(5, axialSpeedMps + axialIncrement);
    const swirlSpeed =
      (this.propeller.swirlGain * propellerTorque) /
      (densityKgPerM3 * this.discAreaM2 * referenceRadius * wakeSpeed);

    // --- the left-turning tendencies ---------------------------------------------------
    const sense = this.engine.rotationSense;

    // Torque reaction: the airframe feels the opposite of what the propeller absorbs.
    /*
     * The airframe reacts to the *propeller's* torque, not the crank's.
     *
     * The gearbox housing is bolted to the aeroplane, so what the airframe ultimately resists is
     * the air's moment on the blades. On a direct-drive trainer the two are the same number and
     * the distinction never came up; on a Merlin the propeller torque is 2.1× the crank's, which
     * is exactly why a Mustang tries to roll onto its back on take-off and a 172 does not.
     */
    out.rollMomentNm =
      -this.propellerShaftTorqueNm(this.revPerSecond, axialSpeedMps, densityKgPerM3) * sense;

    // P-factor: the descending blade makes more thrust, moving the thrust centre sideways.
    const lateralOffset = this.propeller.pFactorGain * diameter * Math.sin(alphaRad) * sense;
    out.yawMomentNm = -lateralOffset * thrust;
    const verticalOffset = this.propeller.pFactorGain * diameter * Math.sin(betaRad) * sense;
    out.pitchMomentNm = -verticalOffset * thrust;

    // The wake rotates with the propeller, so at the fin (above the thrust line) the air moves
    // to starboard for a clockwise propeller. The panel model wants the *aircraft's* velocity
    // relative to that air, which is the opposite sign.
    out.slipstreamSwirlYMps = -swirlSpeed * sense;
    out.slipstreamAxialMps = axialIncrement;

    out.thrustN = thrust;
    out.torqueNm = propellerTorque;
    out.rpm = this.revPerSecond * 60;
    out.powerW = powerW;
    const shaftPower = propellerTorque * this.revPerSecond * 2 * Math.PI;
    out.efficiency = shaftPower > 1 ? (thrust * axialSpeedMps) / shaftPower : 0;
    // No reheat on a piston single, and fuel burn is not modelled — the tanks are not a system
    // this simulator has, and inventing a number here would put a fuel gauge on the panel that
    // meant nothing.
    out.afterburnerFraction = 0;
    out.fuelFlowKgPerS = 0;
    return out;
  }
}
